import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Project } from "@factory-twin/contracts";
import type { ApiClient, SceneSummary } from "../lib/api";
import { OperationsConsole } from "./OperationsConsole";

interface Props {
  api: ApiClient;
  operationsEnabled: boolean;
  onOpenScene: (sceneId: string) => void;
  onOpenLocal: (file: File) => void;
  onLogout: () => void;
}

const statusLabel = { PENDING: "待機中", PROCESSING: "生成中", COMPLETED: "閲覧可能", FAILED: "失敗" } as const;

export function Dashboard({ api, operationsEnabled, onOpenScene, onOpenLocal, onLogout }: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scenes, setScenes] = useState<SceneSummary[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [notice, setNotice] = useState("");
  const [retrainSceneId, setRetrainSceneId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const retrainInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.listProjects().then(({ projects: loaded }) => {
      setProjects(loaded);
      setSelectedId((current) => current ?? loaded[0]?.id ?? null);
    }).catch((error: Error) => setNotice(error.message));
  }, [api]);

  useEffect(() => {
    if (!selectedId) return setScenes([]);
    const load = () => api.listScenes(selectedId).then(({ scenes: next }) => setScenes(next)).catch((error: Error) => setNotice(error.message));
    void load();
    const timer = window.setInterval(load, 10_000);
    return () => window.clearInterval(timer);
  }, [api, selectedId]);

  async function createProject(event: FormEvent) {
    event.preventDefault();
    const { project } = await api.createProject(name, description);
    setProjects((current) => [project, ...current]);
    setSelectedId(project.id);
    setName("");
    setDescription("");
    setShowCreate(false);
  }

  async function upload(file: File, sceneId?: string) {
    if (!selectedId) return;
    setNotice("");
    setUploadProgress(0);
    try {
      await api.uploadVideo(selectedId, file, setUploadProgress, sceneId);
      setNotice(sceneId
        ? "再学習ジョブを登録しました。旧モデル座標系への位置合わせを実行します。"
        : "アップロード完了。3DGS生成ジョブを登録しました。");
      const result = await api.listScenes(selectedId);
      setScenes(result.scenes);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "アップロードに失敗しました");
    } finally {
      window.setTimeout(() => setUploadProgress(null), 1200);
    }
  }

  const selected = projects.find((project) => project.id === selectedId);
  return <main className="app-shell">
    <header className="topbar">
      <div className="brand"><span className="brand-mark">FT</span><span>FACTORY TWIN</span></div>
      <div className="topbar-meta"><span className="live-dot">ONLINE</span><button onClick={onLogout}>ログアウト</button></div>
    </header>
    <aside className="sidebar">
      <div className="section-label">PROJECTS <span>{projects.length.toString().padStart(2, "0")}</span></div>
      <nav>{projects.map((project, index) => <button key={project.id} className={selectedId === project.id ? "selected" : ""} onClick={() => setSelectedId(project.id)}>
        <span className="project-index">{(index + 1).toString().padStart(2, "0")}</span><span><strong>{project.name}</strong><small>{project.description || "説明なし"}</small></span>
      </button>)}</nav>
      <button className="new-project" onClick={() => setShowCreate(true)}>＋ 新規プロジェクト</button>
      <label className="sidebar-local">ローカルモデル<input type="file" accept=".ply,.splat,.ksplat,.spz" onChange={(event) => { const file = event.target.files?.[0]; if (file) onOpenLocal(file); }} /></label>
    </aside>
    <section className="workspace">
      <div className="workspace-heading">
        <div><div className="breadcrumb">WORKSPACE / {selected?.id.slice(0, 8).toUpperCase() ?? "NO PROJECT"}</div><h1>{selected?.name ?? "プロジェクトを作成"}</h1><p>{selected?.description ?? "3Dデジタルツインの作成を開始してください。"}</p></div>
        <button className="upload-button" disabled={!selectedId} onClick={() => fileInput.current?.click()}>動画をアップロード <span>↑</span></button>
        <input ref={fileInput} hidden type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file); }} />
        <input ref={retrainInput} hidden type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(event) => { const file = event.target.files?.[0]; if (file && retrainSceneId) void upload(file, retrainSceneId); event.currentTarget.value = ""; setRetrainSceneId(null); }} />
      </div>
      {uploadProgress !== null && <div className="progress-card"><span style={{ width: `${uploadProgress}%` }} /><strong>UPLOAD {uploadProgress}%</strong></div>}
      {notice && <div className="notice">{notice}</div>}
      <div className="metric-row">
        <article><span>SCENES</span><strong>{scenes.length.toString().padStart(2, "0")}</strong><small>登録シーン</small></article>
        <article><span>READY</span><strong>{scenes.filter((scene) => scene.status === "COMPLETED").length.toString().padStart(2, "0")}</strong><small>閲覧可能</small></article>
        <article><span>PROCESS</span><strong>{scenes.filter((scene) => scene.status === "PROCESSING" || scene.status === "PENDING").length.toString().padStart(2, "0")}</strong><small>処理中</small></article>
      </div>
      <div className="scene-section">
        <div className="section-title"><h2>デジタルツイン</h2><span>LAST SYNC / NOW</span></div>
        {scenes.length === 0 ? <div className="empty-scene">
          <div className="scan-cube"><i /><i /><i /></div><h3>まだシーンがありません</h3><p>スマートフォンで撮影した動画をアップロードすると、生成パイプラインが自動で開始します。</p><button disabled={!selectedId} onClick={() => fileInput.current?.click()}>最初の動画を追加</button>
        </div> : <div className="scene-grid">{scenes.map((scene) => <article className="scene-card" key={scene.id}>
          <div className="scene-preview"><div className="radar" /><span>{scene.id.slice(0, 8)}</span></div>
          <div className="scene-info"><span className={`status ${scene.status.toLowerCase()}`}>{statusLabel[scene.status]}</span><h3>Scene {scene.id.slice(0, 6).toUpperCase()}</h3><p>{new Date(scene.createdAt).toLocaleString("ja-JP")}</p>{scene.alignmentStatus === "NEW_FRAME" && <p className="coordinate-changed">座標系変更・注釈の確認が必要</p>}{scene.status === "FAILED" && scene.errorMessage && <p className="scene-error">{scene.errorMessage}</p>}<div className="scene-actions"><button disabled={!scene.hasModel} onClick={() => onOpenScene(scene.id)}>{scene.hasModel ? "3Dで開く →" : scene.status === "FAILED" ? "生成に失敗しました" : "処理を待っています"}</button><button disabled={!scene.hasModel || scene.status !== "COMPLETED"} onClick={() => { setRetrainSceneId(scene.id); window.setTimeout(() => retrainInput.current?.click()); }}>再学習</button></div></div>
        </article>)}</div>}
      </div>
      {operationsEnabled && <OperationsConsole api={api} />}
    </section>
    {showCreate && <div className="modal-backdrop"><form className="modal" onSubmit={(event) => void createProject(event)}><div className="section-label">NEW PROJECT / CONFIG</div><h2>プロジェクトを作成</h2><label>名称<input value={name} onChange={(event) => setName(event.target.value)} autoFocus required maxLength={120} /></label><label>説明<textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2000} /></label><div><button type="button" onClick={() => setShowCreate(false)}>キャンセル</button><button className="primary-button">作成する</button></div></form></div>}
  </main>;
}
